'use client';

import { useLocalization } from '@/hooks/useLocalization';
import { useRouter } from '@/i18n/routing';
import { useQuery } from '@tanstack/react-query';
import { SearchProduct } from '@winelivery-org/wl-client-sdk-js/dist/models/search';
import { useFormatter, useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { notFound } from 'next/navigation';
import { useEffect } from 'react';
import { Button } from '@/components/design-system';
import {
  ChevronLeft,
  Chrono as IconChrono,
  Location as IconLocation,
} from '@/components/icons';
import * as GTM from '@/lib/GTM';
import * as Targeting360 from '@/lib/targeting360';
import { getStoreProducts } from '@/services/search';
import useShop from '@/stores/shop/useContext';
import useFilters from '../../components/Shop/hooks/useFilters';
import AddressModal from '../../components/commons/AddressModal/AddressModal';
import DeliveryTimeLabel from '../../components/commons/AddressModal/DeliveryTimeLabel';
import useAddressSearch from '../../components/commons/AddressModal/hook';
import useProduct from '../../components/hooks/useProduct';
import { useFindFacetsQuery } from '../../components/query/useFindFacetsQuery';
import useFindProductByIdQuery from '../../components/query/useFindProductByIdQuery';
import ButtonShare from './ButtonShare';
import ButtonWishList from './ButtonWishList';
import ProductDetailSectionDescription from './ProductDetailSectionDescription';
import ProductDetailSectionOccasions from './ProductDetailSectionOccasions';
import ProductDetailSectionSpecifications from './ProductDetailSectionSpecifications';
import ProductDetailSkeleton from './ProductDetailSkeleton';
import RelatedProducts from './RelatedProducts';
import Image from 'next/image';

const ButtonCart = dynamic(() => import('./ButtonCart'), {
  ssr: false,
});

function ProductDetail({ product: initialData }: { product: SearchProduct }) {
  const t = useTranslations('common');
  const format = useFormatter();
  const tShop = useTranslations('shop');
  const router = useRouter();

  const { updateFacets } = useShop((s) => s);
  const { filters } = useFilters();
  const { az, location } = useLocalization();

  const { updateGlobalLocation } = useAddressSearch('');

  const {
    data: product,
    isLoading: isProductLoading,
    isError,
  } = useFindProductByIdQuery(initialData.id, filters, initialData);

  const { quantityInCart } = useProduct(product!);
  const relatedFilters = {
    ...filters,
    limit: 10,
    category: product?.category ? [product.category.slug] : undefined,
    subcategory: product?.subcategory ? [product.subcategory.slug] : undefined,
  };

  const { data: relatedProducts } =
    useQuery({
      queryKey: ['related-products', initialData.id, relatedFilters],
      queryFn: () => getStoreProducts(relatedFilters),
      enabled: !!product,
    });

  const { data: facets } = useFindFacetsQuery();

  useEffect(() => {
    updateFacets(facets);
  }, [facets]);

  useEffect(() => {
    if (!product) return;

    GTM.viewContent(product);
    Targeting360.trackProductPage(product);
    GTM.detail(product);
  }, []);

  const scalapayThreshold =
    (product!.discountedPrice || product!.price) * (quantityInCart || 1);

  function goBack() {
    if (window.history.length === 1) {
      router.replace('/shop');
      return;
    }
    router.back();
  }

  if (isProductLoading) {
    return <ProductDetailSkeleton />;
  }

  if (isError) {
    return notFound();
  }

  if (!product) {
    return null;
  }

  return (
    <>
      <div className="container mx-auto pt-8 px-4 lg:px-6 xl:px-8 hidden sm:flex sm:justify-between sm:items-center">
        <div>
          <Button palette="gray" theme="outline" onClick={goBack}>
            <ChevronLeft className="shrink-0" />
            <span>{t('action.back')}</span>
          </Button>
        </div>
      </div>

      <div
        className="container mx-auto py-4 md:py-8 grid grid-cols-1 md:grid-cols-5 gap-8 px-4 lg:px-6 xl:px-8">
      <div className="md:col-span-3 md:space-y-8">
          <div className="bg-white h-[300px] md:h-[550px] relative">
            <Image
              fill
              className="object-contain"
              src={product.imageUrl || ''}
              alt={product.name}
            />
          </div>

          <div className="block md:hidden py-6 md:py-0">
            <h1 className="text-4xl font-bold text-greyscale-title tracking-tighter mb-1 p-0">
              {product.name}
            </h1>
            <h3 className="text-base font-medium tracking-tight text-greyscale-body m-0 p-0">
              {product.producer}
            </h3>
            <div className="space-x-3 mt-3">
              <ButtonWishList product={product} />
              <ButtonShare product={product} />
            </div>

            <div className="mt-6">
              <AddressModal
                showSavedAddresses
                showLastAddress
                onChangeAddress={updateGlobalLocation}
                renderTrigger={({ open }) => (
                  <div
                    className="p-4 space-y-2 border border-greyscale-separator rounded-2xl"
                    onClick={() => open()}
                  >
                    <div className="flex items-center gap-3">
                      <IconLocation className="text-greyscale-placeholder shrink-0" />
                      <div className="flex-1 overflow-hidden">
                        <div className="text-xs text-greyscale-placeholder font-medium tracking-tight">
                          {t('address.title')}
                        </div>
                        <div className="flex-1 text-base font-medium tracking-tight text-greyscale-body truncate">
                          {location?.formattedAddress || t('address.insert')}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <IconChrono className="text-greyscale-placeholder shrink-0" />
                      <div className="flex-1 overflow-hidden">
                        <div className="text-xs text-greyscale-placeholder font-medium tracking-tight">
                          {tShop('deliveryTime.title')}
                        </div>
                        <div className="flex-1 text-base font-medium tracking-tight text-greyscale-body truncate">
                          <DeliveryTimeLabel />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          </div>

          <div className="divide-y divide-greyscale-separator">
            <ProductDetailSectionDescription product={product} />

            <ProductDetailSectionOccasions product={product} />

            <ProductDetailSectionSpecifications product={product} />
          </div>
        </div>
        <div className="hidden md:block md:col-span-2">
          <div className="sticky top-40 rounded-2xl shadow-sm border-greyscale-separator p-8 space-y-3 bg-greyscale-offwhite">
            <h1 className="text-5xl font-bold text-greyscale-title tracking-tighter m-0 p-0">
              {product.name}
            </h1>
            <h3 className="text-xl font-medium tracking-tight text-greyscale-body m-0 p-0">
              {product.producer}
            </h3>
            <div className="flex items-baseline gap-3 text-3xl font-bold tracking-tighter text-greyscale-body m-0 p-0">
              {!!product.discountedPrice && (
                <span className="line-through text-2xl font-medium text-greyscale-label">
                  {format.number(product.price, {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </span>
              )}
              <span>
                {format.number(product.discountedPrice || product.price, {
                  style: 'currency',
                  currency: 'EUR',
                })}
              </span>
            </div>
            {az?.paymentMethods.includes('scalapay') && (
              <div className="mt-2">
                {/*@ts-ignore*/}
                <scalapay-widget
                  frequency-number="30"
                  number-of-installments="3"
                  hide={'false'}
                  hide-price="false"
                  min="5"
                  max="1500"
                  amount={scalapayThreshold}
                  currency-position="before"
                  currency-display="symbol"
                  logo-size="100"
                  theme="primary"
                  locale="it"
                />
              </div>
            )}
            <div className="flex items-center gap-4 pt-2">
              <div className="flex-1">
                <ButtonCart product={product} />
              </div>
              <ButtonWishList product={product} />
              <ButtonShare product={product} />
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto py-8 px-4 lg:px-6 xl:px-8">
        <RelatedProducts products={relatedProducts?.items || []} />
      </div>

      <div className="md:hidden sticky inset-x-0 bottom-0 z-10 bg-white border-t border-greyscale-separator ring-2 ring-gray-200/30">
        <div className="container mx-auto p-4 flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xs text-greyscale-body">
              {tShop('detail.price.title')}
            </div>
            <div className="flex items-baseline gap-2 text-2xl font-bold mb-0 text-greyscale-body tracking-tight">
              {!!product.discountedPrice && (
                <span className="line-through text-xl text-greyscale-label font-medium">
                  {format.number(product.price, {
                    style: 'currency',
                    currency: 'EUR',
                  })}
                </span>
              )}
              <span>
                {format.number(product.price, {
                  style: 'currency',
                  currency: 'EUR',
                })}
              </span>
            </div>
          </div>
          <div>
            <ButtonCart product={product} />
          </div>
        </div>
      </div>
    </>
  );
}

export default ProductDetail;
